/* eslint-disable no-alert */
import React, { useState } from 'react';
import * as Yup from 'yup';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import classNames from 'classnames';
import { useFormik } from 'formik';

import Axios from '@/utils/axios';
import { ArrRight } from '@/icons/index';
import {
  Button,
  CheckboxWithTitle,
  Input,
  RadioWithTitle,
  SectionTitle,
  Textarea,
  TextBlock,
  InformationModal,
} from '@/ui/index';

import styles from './Form.module.scss';

const Form = ({ data }) => {
  const { t, lang } = useTranslation();
  const router = useRouter();
  const FormSchema = Yup.object().shape({
    entrepreneur_type: Yup.string().required(t('common:required')),
    partner_type: Yup.array()
      .min(1, t('common:required'))
      .required(t('common:required')),
    personInformation: Yup.object().shape({
      full_name: Yup.string()
        .min(2, t('common:required'))
        .max(150, t('common:required'))
        .required(t('common:required')),
      id_number: Yup.string()
        .min(2, t('common:required'))
        .max(11)
        .required(t('common:required')),
      date_of_birthday: Yup.date('dd.mm.yyyy').required(t('common:required')),
      address: Yup.string()
        .min(2, t('common:required'))
        .max(150, t('common:required'))
        .required(t('common:required')),
    }),
    generalInformation: Yup.object().shape({
      experience_term: Yup.string()
        .min(6, t('common:required'))
        .required(t('common:required')),
      current_work_activities: Yup.string()
        .min(2, t('common:required'))
        .required(t('common:required')),
      shop_address: Yup.string()
        .min(2, t('common:required'))
        .required(t('common:required')),
      area_of_the_shop: Yup.string()
        .min(2, t('common:required'))
        .required(t('common:required')),
      other_shops_in_vicinity: Yup.string()
        .min(2, t('common:required'))
        .required(t('common:required')),
      contact_phone: Yup.string()
        .min(12, t('common:required'))
        .max(12, t('common:required'))
        .required(t('common:required')),
      email: Yup.string()
        .email(t('common:Invalid email'))
        .required(t('common:Invalid email')),
    }),
  });

  const formik = useFormik({
    initialValues: {
      entrepreneur_type: '',
      partner_type: [],
      personInformation: {
        full_name: '',
        id_number: '',
        date_of_birthday: '',
        address: '',
      },
      generalInformation: {
        experience_term: '',
        current_work_activities: '',
        shop_address: '',
        area_of_the_shop: '',
        other_shops_in_vicinity: '',
        contact_phone: '',
        email: '',
        comments: '',
      },
    },
    validationSchema: FormSchema,
    onSubmit: async (values, { resetForm }) => {
      const data = {
        ...values.personInformation,
        ...values.generalInformation,
        entrepreneur_type: values.entrepreneur_type,
        partner_type: values.partner_type.join(', '),
      };
      await onSend(data);
      resetForm();
    },
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const onSend = async (data) => {
    try {
      await Axios.post('partnership-requests/', data);
      setShowSuccess(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };
  const onchangePartnerType = (value, index) => {
    if (value === 'Others') {
      formik.setFieldValue(
        'partner_type',
        formik.values.partner_type.includes(value) ? [] : [value],
      );
    } else {
      if (formik.values.partner_type.includes('Others')) {
        formik.setFieldValue('partner_type', []);
      }
      formik.setFieldValue(
        `partner_type[${index}]`,
        formik.values.partner_type[index] ? null : value,
      );
    }
  };
  const modalHandler = () => {
    setShowSuccess(false);
  };
  React.useEffect(() => {
    formik.validateForm();
  }, [lang]);

  return (
    <>
      <div
        className={`dark-form ${styles.mainWrapper}`}
        itemScope
        itemType='https://schema.org/Action'
      >
        <div className={classNames(['wrapper section', styles.wrapper])}>
          <SectionTitle titleModify='title--large'>{data?.title}</SectionTitle>
          <TextBlock className={classNames('text--large', styles.text)}>
            {data?.description}
          </TextBlock>
          <form className={styles.form} onSubmit={formik.handleSubmit}>
            <div className={styles.item}>
              <SectionTitle
                titleModify={'title--small'}
                className={styles.title}
              >
                {t('common:partnership.entrepreneur')}
              </SectionTitle>
              <div className={styles.grid}>
                <RadioWithTitle
                  onChange={() =>
                    formik.setFieldValue('entrepreneur_type', 'Individual')
                  }
                  name={'type'}
                  reverse
                >
                  {t('common:partnership.individual')}
                </RadioWithTitle>
                <RadioWithTitle
                  onChange={() =>
                    formik.setFieldValue('entrepreneur_type', 'Company')
                  }
                  name={'type'}
                  reverse
                >
                  {t('common:partnership.company')}
                </RadioWithTitle>
              </div>
            </div>
            <div className={styles.item}>
              <SectionTitle
                titleModify={'title--small'}
                className={styles.title}
              >
                {t('common:partnership.partnerType')}
              </SectionTitle>
              <div className={classNames(styles.grid, styles.grid3)}>
                <CheckboxWithTitle
                  reverse
                  onChange={() => onchangePartnerType('Distributor', 1)}
                  name={'Distributor'}
                  checked={formik.values.partner_type.includes('Distributor')}
                >
                  {t('common:partnership.distributor')}
                </CheckboxWithTitle>
                <CheckboxWithTitle
                  reverse
                  onChange={() => onchangePartnerType('Dealer', 2)}
                  name={'Dealer'}
                  checked={formik.values.partner_type.includes('Dealer')}
                >
                  {t('common:partnership.dealer')}
                </CheckboxWithTitle>
                <CheckboxWithTitle
                  reverse
                  onChange={() => onchangePartnerType('Others', 0)}
                  name={'Others'}
                  checked={formik.values.partner_type.includes('Others')}
                >
                  {t('common:partnership.other')}
                </CheckboxWithTitle>
              </div>
            </div>
            <div className={styles.item}>
              <SectionTitle
                titleModify={'title--small'}
                className={styles.title}
              >
                {t('common:partnership.persoralInformation')}
              </SectionTitle>
              <div className={styles.grid}>
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur('personInformation.full_name')}
                  value={formik.values.personInformation.full_name}
                  onChange={(value) =>
                    formik.setFieldValue('personInformation.full_name', value)
                  }
                  placeholder={t('common:fullname')}
                  label={t('common:partnership.fullName')}
                  error={
                    formik.touched.personInformation?.full_name &&
                    formik.errors.personInformation?.full_name
                  }
                />
                <Input
                  className={styles.customInput}
                  maxlength='11'
                  onBlur={formik.handleBlur('personInformation.id_number')}
                  value={formik.values.personInformation.id_number}
                  type='number'
                  onChange={(value) =>
                    formik.setFieldValue('personInformation.id_number', value)
                  }
                  placeholder={'AA000000'}
                  label={t('common:partnership.idNumber')}
                  error={
                    formik.touched.personInformation?.id_number &&
                    formik.errors.personInformation?.id_number
                  }
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur(
                    'personInformation.date_of_birthday',
                  )}
                  value={formik.values.personInformation.date_of_birthday}
                  type='number'
                  onChange={(value) =>
                    formik.setFieldValue(
                      'personInformation.date_of_birthday',
                      value,
                    )
                  }
                  placeholder={'01.01.1980'}
                  label={t('common:partnership.birth')}
                  mask={'99.99.9999'}
                  error={
                    formik.touched.personInformation?.date_of_birthday &&
                    formik.errors.personInformation?.date_of_birthday
                  }
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur('personInformation.address')}
                  value={formik.values.personInformation.address}
                  type='validateGeneralInput'
                  onChange={(value) =>
                    formik.setFieldValue('personInformation.address', value)
                  }
                  error={
                    formik.touched.personInformation?.address &&
                    formik.errors.personInformation?.address
                  }
                  placeholder={t('common:partnership.addresDetail')}
                  label={t('common:partnership.address')}
                />
              </div>
            </div>
            <div className={styles.item}>
              <SectionTitle
                titleModify={'title--small'}
                className={styles.title}
              >
                {t('common:partnership.generalInformation')}
              </SectionTitle>
              <div className={classNames(styles.grid, styles.information)}>
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur(
                    'generalInformation.experience_term',
                  )}
                  value={formik.values.generalInformation.experience_term}
                  type='validateGeneralInput'
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.experience_term',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation?.experience_term &&
                    formik.errors.generalInformation?.experience_term
                  }
                  placeholder={t('common:partnership.experiencePlaceholder')}
                  label={t('common:partnership.experience')}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur(
                    'generalInformation.current_work_activities',
                  )}
                  value={
                    formik.values.generalInformation.current_work_activities
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.current_work_activities',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation
                      ?.current_work_activities &&
                    formik.errors.generalInformation?.current_work_activities
                  }
                  placeholder={t('common:partnership.activitiesPlaceholder')}
                  label={t('common:partnership.activities')}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur('generalInformation.shop_address')}
                  value={formik.values.generalInformation.shop_address}
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.shop_address',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation?.shop_address &&
                    formik.errors.generalInformation?.shop_address
                  }
                  placeholder={t('common:partnership.shopAddressPlaceholder')}
                  label={t('common:partnership.shopAddress')}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur(
                    'generalInformation.area_of_the_shop',
                  )}
                  value={formik.values.generalInformation.area_of_the_shop}
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.area_of_the_shop',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation?.area_of_the_shop &&
                    formik.errors.generalInformation?.area_of_the_shop
                  }
                  placeholder={t('common:partnership.areaShopPlaceholder')}
                  label={t('common:partnership.areaShop')}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur(
                    'generalInformation.other_shops_in_vicinity',
                  )}
                  value={
                    formik.values.generalInformation.other_shops_in_vicinity
                  }
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.other_shops_in_vicinity',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation
                      ?.other_shops_in_vicinity &&
                    formik.errors.generalInformation?.other_shops_in_vicinity
                  }
                  placeholder={t('common:partnership.otherAreaPlaceholder')}
                  label={t('common:partnership.otherArea')}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur('generalInformation.contact_phone')}
                  value={formik.values.generalInformation.contact_phone}
                  onChange={(value) =>
                    formik.setFieldValue(
                      'generalInformation.contact_phone',
                      value,
                    )
                  }
                  error={
                    formik.touched.generalInformation?.contact_phone &&
                    formik.errors.generalInformation?.contact_phone
                  }
                  placeholder={t('common:partnership.phonePlaceholder')}
                  type={'tel'}
                  label={t('common:partnership.phone')}
                  mask={'+\\9\\9\\4-99-999-99-99'}
                />
                <Input
                  className={styles.customInput}
                  onBlur={formik.handleBlur('generalInformation.email')}
                  value={formik.values.generalInformation.email}
                  onChange={(value) =>
                    formik.setFieldValue('generalInformation.email', value)
                  }
                  error={
                    formik.touched.generalInformation?.email &&
                    formik.errors.generalInformation?.email
                  }
                  placeholder={t('common:partnership.email')}
                  type='email'
                  label={'E-mail'}
                />
                <Textarea
                  className={styles.customInput}
                  value={formik.values.generalInformation.comments}
                  onChange={(value) =>
                    formik.setFieldValue('generalInformation.comments', value)
                  }
                  placeholder={t('common:partnership.commentsPlaceholder')}
                  label={t('common:partnership.comments')}
                />
              </div>
              <Button className={styles.submit} type={'submit'}>
                {t('common:partnership.send')} <ArrRight width={20} />
              </Button>
            </div>
          </form>
        </div>
      </div>
      <InformationModal
        type={'white'}
        description={t('common:partnership.sentsuccess')}
        active={showSuccess}
        onClose={modalHandler}
      />
    </>
  );
};

export default Form;
